<script setup lang="ts" generic="T extends object">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter, type LocationQueryValue } from 'vue-router'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import PiIcon, { type PiIconName } from '@uiKit/elements/PiIcon.vue'
import Popover from '@uiKit/elements/Popover.vue'
import Skeleton from '@uiKit/elements/Skeleton.vue'
import Select from '@uiKit/elements/Select.vue'
import DataTable, { type DataTableColumn } from '@uiKit/elements/DataTable.vue'
import SearchInput from '@uiKit/components/SearchInput.vue'
import localStorageService from '@core/persistence/localStorage/localStorageService'
import type { SelectOption } from '@shared/types'

export interface TableColumn {
  key: string
  label: string
  width?: string
  minWidth?: string
  align?: 'left' | 'center' | 'right'
  /** Include in local search match (default true). */
  searchable?: boolean
  /** Allow click-to-sort affordance. Defaults to false — sort is opt-in here. */
  sortable?: boolean
  /** Make this column the drag handle for row reorder. Requires `reorderable` on the DataTableV2. */
  rowReorder?: boolean
}

export interface TableAction<R> {
  label: string
  icon?: PiIconName
  /** Marks the item as destructive (red text). */
  danger?: boolean
  /** Hide the item for rows that don't apply. */
  hidden?: (row: R) => boolean
  /** Disable the item for rows that don't apply (still rendered, greyed). */
  disabled?: (row: R) => boolean
  onClick: (row: R) => void
}

export interface TableChangeEvent {
  page: number
  pageSize: number
  search: string
  sort: string
}

interface Props {
  columns: TableColumn[]
  rows: T[]
  loading?: boolean
  rowKey?: string

  // Header
  title?: string
  subtitle?: string

  // Search
  /** Show the built-in search input. Default `true`. */
  searchable?: boolean
  searchPlaceholder?: string
  /** Column keys to match against. Defaults to all `searchable !== false` columns. */
  searchFields?: string[]
  /** v-model:search — controlled query. */
  search?: string
  /**
   * When `true` (default), the table filters `rows` locally. Pass `false`
   * when the parent owns filtering (e.g. server-side search) — the table
   * still owns the input, it just emits `update:search` / `change`.
   */
  localSearch?: boolean

  // Pagination
  /**
   * When `true` (default), the table paginates `rows` locally. Pass `false`
   * when the parent owns paging — the table emits `update:page` / `change`
   * and the parent supplies the page slice via `rows`.
   */
  localPagination?: boolean
  pageSize?: number
  /** v-model:page — 1-based. */
  page?: number
  /** Authoritative total record count (required for server-side mode). */
  total?: number
  /** Page size choices in the footer dropdown. Defaults to [20, 50, 100]. */
  pageSizeOptions?: number[]
  /**
   * When provided, the kit reads/writes `pageSize` from `localStorage[<key>]`
   * via `localStorageService`. Persists the operator's choice across sessions.
   */
  pageSizeStorageKey?: string
  /**
   * When `true`, sync `page` and `pageSize` with `route.query` (read on
   * mount, push via `router.replace`). Defaults to `false`.
   */
  urlSync?: boolean
  /**
   * Namespace for the URL query keys when 2+ tables live on the same page.
   * When provided, keys become `${prefix}Page` / `${prefix}PageSize`.
   * When absent, plain `page` / `pageSize`.
   */
  urlSyncPrefix?: string

  // Sort dropdown
  /**
   * When provided, render a sort `<Select>` in the header. The table does
   * NOT sort rows — it emits `@update:sort` and the consumer either sorts
   * `rows` locally or passes the value into the API filters.
   */
  sortOptions?: SelectOption[]
  /** v-model:sort — currently selected sort value. */
  sort?: string
  /** Placeholder shown when no `sort` value is selected. */
  sortPlaceholder?: string

  // Filter trigger
  /**
   * Show a filter icon button right of the search. The table does NOT own
   * filter UI — clicking emits `@filter-click` and the consumer renders its
   * own filter surface (drawer, dialog, popover).
   */
  filterable?: boolean
  /** When > 0, renders a small count badge over the filter icon. */
  filterCount?: number

  // Row interaction
  /** Action items rendered behind a three-dot popover per row. */
  actions?: TableAction<T>[]
  /** When no actions are given, treat rows as clickable (chevron + row-click). */
  clickable?: boolean

  // Row reorder
  /** Enable drag-to-reorder rows. Requires a column with `rowReorder: true`. */
  reorderable?: boolean

  // Empty state
  emptyTitle?: string
  emptySubtitle?: string

  // Skeleton (loading state)
  /** How many skeleton rows to render while `loading` is true. Default `5`. */
  skeletonRows?: number

  // Density / chrome
  density?: 'compact' | 'default' | 'comfortable'
  flat?: boolean
  /** Scroll horizontally instead of clipping columns that don't fit. */
  scrollable?: boolean
  /**
   * A short, fixed table (a fee breakdown) with no footer — no summary, page
   * size or Prev / Next — showing every row. Default false.
   */
  hideFooter?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  searchable: true,
  searchPlaceholder: 'Search',
  localSearch: true,
  localPagination: true,
  pageSize: 50,
  page: 1,
  sortPlaceholder: 'Sort by',
  filterable: false,
  filterCount: 0,
  clickable: false,
  skeletonRows: 5,
  density: 'default',
  flat: false,
  scrollable: false,
  emptyTitle: 'No results',
  emptySubtitle: 'Nothing to show here yet.',
  pageSizeOptions: () => [20, 50, 100],
  urlSync: false,
})

const emit = defineEmits<{
  (e: 'update:search', value: string): void
  (e: 'update:page', value: number): void
  (e: 'update:pageSize', value: number): void
  (e: 'update:sort', value: string): void
  (e: 'row-click', row: T): void
  (e: 'filter-click', event: Event): void
  (e: 'change', value: TableChangeEvent): void
  (e: 'row-reorder', event: { value: T[] }): void
}>()

function onFilterClick(event: Event): void {
  emit('filter-click', event)
}

// Inline-cast helper — keeps templates Prettier-friendly. Prettier's HTML
// parser misreads `Record<string, unknown>` inside `{{ … }}` as an opening
// `<unknown>` tag, which breaks `prettier --write`. Centralising the cast
// here keeps the template free of inline TS generics.
function cellValue(row: T, key: string): unknown {
  return (row as Record<string, unknown>)[key]
}

// ── Search state ────────────────────────────────────────────────────────────
const internalSearch = ref(props.search ?? '')
watch(
  () => props.search,
  (v) => {
    if (typeof v === 'string' && v !== internalSearch.value) internalSearch.value = v
  },
)

// ── Page state ──────────────────────────────────────────────────────────────
const currentPage = ref(props.page)
watch(
  () => props.page,
  (v) => {
    if (v !== currentPage.value) currentPage.value = v
  },
)

// Internal mirror of pageSize so the kit can drive both the UI and the
// URL/localStorage synchronisation reactively. The parent's prop wins;
// the kit just emits update:pageSize and re-syncs.
const currentPageSize = ref(props.pageSize)
watch(
  () => props.pageSize,
  (v) => {
    if (v !== currentPageSize.value) currentPageSize.value = v
  },
)

// ── Sort state ──────────────────────────────────────────────────────────────
const internalSort = ref(props.sort ?? '')
watch(
  () => props.sort,
  (v) => {
    if (typeof v === 'string' && v !== internalSort.value) internalSort.value = v
  },
)

const searchValue = computed({
  get: () => internalSearch.value,
  set: (v) => {
    internalSearch.value = v
    emit('update:search', v)
    if (currentPage.value !== 1) {
      currentPage.value = 1
      emit('update:page', 1)
    }
    emit('change', {
      page: 1,
      pageSize: currentPageSize.value,
      search: v,
      sort: internalSort.value,
    })
  },
})

const sortValue = computed({
  get: () => internalSort.value,
  set: (v) => {
    internalSort.value = v
    emit('update:sort', v)
    if (currentPage.value !== 1) {
      currentPage.value = 1
      emit('update:page', 1)
    }
    emit('change', {
      page: 1,
      pageSize: currentPageSize.value,
      search: internalSearch.value,
      sort: v,
    })
  },
})

// ── Filtered rows (local search) ───────────────────────────────────────────
const searchFieldKeys = computed<string[]>(() => {
  if (props.searchFields?.length) return props.searchFields
  return props.columns.filter((c) => c.searchable !== false).map((c) => c.key)
})

const filteredRows = computed<T[]>(() => {
  if (!props.localSearch) return props.rows
  const q = searchValue.value.trim().toLowerCase()
  if (!q) return props.rows
  return props.rows.filter((row) =>
    searchFieldKeys.value.some((field) => {
      const v = (row as Record<string, unknown>)[field]
      return v != null && String(v).toLowerCase().includes(q)
    }),
  )
})

// ── Pagination ──────────────────────────────────────────────────────────────
const totalRecords = computed(() =>
  typeof props.total === 'number' ? props.total : filteredRows.value.length,
)
const totalPages = computed(() =>
  totalRecords.value === 0 ? 0 : Math.ceil(totalRecords.value / currentPageSize.value),
)

const visibleRows = computed<T[]>(() => {
  if (!props.localPagination || props.hideFooter) return filteredRows.value
  const start = (currentPage.value - 1) * currentPageSize.value
  return filteredRows.value.slice(start, start + currentPageSize.value)
})

// While loading, feed DataTable a synthetic row set so the table chrome
// (headers + row rhythm) renders with skeleton placeholders instead of a
// spinner. Cell slots short-circuit to <Skeleton> when `loading` is true.
const SKELETON_FLAG = '__app_table_skeleton__'
const skeletonRowsData = computed<T[]>(() =>
  Array.from(
    { length: props.skeletonRows },
    (_, i) => ({ [SKELETON_FLAG]: true, __i: i }) as unknown as T,
  ),
)
const tableRows = computed<T[]>(() => (props.loading ? skeletonRowsData.value : visibleRows.value))

const summary = computed(() => {
  const total = totalRecords.value
  if (total === 0) return 'No results'
  return `Showing ${visibleRows.value.length} of ${total.toLocaleString()}`
})

// ── Direct page-jump input ──────────────────────────────────────────────────
// Operators can type a page number and press Enter (or blur the input) to
// jump directly. Invalid values (non-numeric, out of range) silently revert
// to the current page — no error styling, no toast. Min `1`, max
// `totalPages`. Width fits ~3 digits.
const pageInputValue = ref(String(currentPage.value))
watch(
  () => currentPage.value,
  (v) => {
    pageInputValue.value = String(v)
  },
)

function commitPageInput(): void {
  const parsed = Number.parseInt(pageInputValue.value, 10)
  if (
    Number.isFinite(parsed) &&
    parsed >= 1 &&
    (totalPages.value === 0 || parsed <= totalPages.value) &&
    parsed !== currentPage.value
  ) {
    goToPage(parsed)
    return
  }
  // Revert silently — operator typed garbage or an out-of-range value.
  pageInputValue.value = String(currentPage.value)
}

function handlePageInputKeydown(event: KeyboardEvent): void {
  if (event.key === 'Enter') {
    ;(event.target as HTMLInputElement).blur()
  }
}

// Page-size selector options. If the consumer's current `pageSize` is not
// in the configured `pageSizeOptions` (e.g. legacy `pageSize=10`), prepend
// it so the selector renders correctly — don't snap to a nearby value.
const pageSizeSelectOptions = computed<SelectOption[]>(() => {
  const set = new Set<number>(props.pageSizeOptions)
  const all: number[] = []
  if (!set.has(currentPageSize.value)) {
    all.push(currentPageSize.value)
  }
  for (const n of props.pageSizeOptions) all.push(n)
  return all.map((n) => ({ label: String(n), value: String(n) }))
})

const pageSizeValue = computed({
  get: () => String(currentPageSize.value),
  set: (next) => {
    const parsed = Number.parseInt(next, 10)
    if (!Number.isFinite(parsed) || parsed <= 0) return
    if (parsed === currentPageSize.value) return
    setPageSize(parsed)
  },
})

const canPrev = computed(() => currentPage.value > 1)
const canNext = computed(() => currentPage.value < totalPages.value)

function goToPage(p: number): void {
  if (p < 1 || (totalPages.value > 0 && p > totalPages.value)) return
  currentPage.value = p
  emit('update:page', p)
  emit('change', {
    page: p,
    pageSize: currentPageSize.value,
    search: searchValue.value,
    sort: sortValue.value,
  })
}
function goPrev(): void {
  if (canPrev.value) goToPage(currentPage.value - 1)
}
function goNext(): void {
  if (canNext.value) goToPage(currentPage.value + 1)
}

// ── Page-size selector + localStorage persistence ──────────────────────────
function persistPageSize(value: number): void {
  if (!props.pageSizeStorageKey) return
  localStorageService.setItem(props.pageSizeStorageKey, String(value))
}

function setPageSize(next: number): void {
  if (next === currentPageSize.value) return
  currentPageSize.value = next
  emit('update:pageSize', next)
  persistPageSize(next)
  // Reset to page 1 so the operator never lands on an empty trailing page.
  if (currentPage.value !== 1) {
    currentPage.value = 1
    emit('update:page', 1)
  }
  emit('change', {
    page: 1,
    pageSize: next,
    search: searchValue.value,
    sort: sortValue.value,
  })
}

// ── URL sync (optional, opt-in via `urlSync`) ──────────────────────────────
// `useRoute()` / `useRouter()` throw if the kit is mounted outside a routed
// context. Wrap in try/catch so `urlSync` gracefully no-ops in that case.
type RouteLike = ReturnType<typeof useRoute> | null
type RouterLike = ReturnType<typeof useRouter> | null
let route: RouteLike = null
let router: RouterLike = null
try {
  route = useRoute()
  router = useRouter()
} catch {
  route = null
  router = null
}

const urlSyncEnabled = computed(() => props.urlSync && route !== null && router !== null)

function urlKey(name: 'page' | 'pageSize'): string {
  if (!props.urlSyncPrefix) return name
  // Prefix camelCases the keys: `${prefix}Page` / `${prefix}PageSize`.
  return `${props.urlSyncPrefix}${name.charAt(0).toUpperCase()}${name.slice(1)}`
}

function readNumberQuery(value: LocationQueryValue | LocationQueryValue[]): number | null {
  if (Array.isArray(value)) return null
  if (value == null) return null
  const parsed = Number.parseInt(value, 10)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

// Guard ref to avoid feedback loops (URL → emit → parent updates ref → URL …).
const suppressRouteWrite = ref(false)

async function pushQueryUpdate(updates: Record<string, string>): Promise<void> {
  if (!urlSyncEnabled.value || !route || !router) return
  await nextTick()
  const nextQuery = { ...route.query, ...updates }
  // Skip the write if nothing actually changed — avoids triggering a
  // navigation that the watcher would then echo back.
  let dirty = false
  for (const [k, v] of Object.entries(updates)) {
    if (route.query[k] !== v) {
      dirty = true
      break
    }
  }
  if (!dirty) return
  suppressRouteWrite.value = true
  try {
    await router.replace({ query: nextQuery })
  } finally {
    suppressRouteWrite.value = false
  }
}

watch(
  () => currentPage.value,
  (v) => {
    if (!urlSyncEnabled.value || suppressRouteWrite.value) return
    void pushQueryUpdate({ [urlKey('page')]: String(v) })
  },
)
watch(
  () => currentPageSize.value,
  (v) => {
    if (!urlSyncEnabled.value || suppressRouteWrite.value) return
    void pushQueryUpdate({ [urlKey('pageSize')]: String(v) })
  },
)

onMounted(() => {
  // localStorage first — the operator's persisted choice.
  if (props.pageSizeStorageKey) {
    const stored = localStorageService.getItem(props.pageSizeStorageKey)
    if (stored !== null) {
      const parsed = Number.parseInt(stored, 10)
      if (Number.isFinite(parsed) && parsed > 0 && parsed !== currentPageSize.value) {
        currentPageSize.value = parsed
        emit('update:pageSize', parsed)
      }
    }
  }

  // URL wins over localStorage (refresh-time operator intent).
  if (urlSyncEnabled.value && route) {
    const urlPageSize = readNumberQuery(route.query[urlKey('pageSize')])
    if (urlPageSize !== null && urlPageSize !== currentPageSize.value) {
      currentPageSize.value = urlPageSize
      emit('update:pageSize', urlPageSize)
      persistPageSize(urlPageSize)
    }
    const urlPage = readNumberQuery(route.query[urlKey('page')])
    if (urlPage !== null && urlPage !== currentPage.value) {
      currentPage.value = urlPage
      emit('update:page', urlPage)
    }
  }
})

// ── Columns (with auto-injected actions / chevron column) ─────────────────
const ACTIONS_KEY = '__actions__'
const showActionsColumn = computed(() => Boolean(props.actions?.length))
const showChevronColumn = computed(() => props.clickable && !showActionsColumn.value)

const dtColumns = computed<DataTableColumn[]>(() => {
  const base: DataTableColumn[] = props.columns.map((c) => ({
    field: c.key,
    header: c.label,
    width: c.width,
    minWidth: c.minWidth,
    align: c.align,
    sortable: c.sortable === true,
    rowReorder: c.rowReorder === true,
  }))
  if (showActionsColumn.value) {
    base.push({ field: ACTIONS_KEY, header: '', width: '56px', align: 'right', sortable: false })
  } else if (showChevronColumn.value) {
    base.push({ field: ACTIONS_KEY, header: '', width: '48px', align: 'right', sortable: false })
  }
  return base
})

// ── Actions popover (single instance, retargeted per row) ─────────────────
const popoverRef = ref<InstanceType<typeof Popover> | null>(null)
const activeRow = ref<T | null>(null)

function openActions(event: Event, row: T): void {
  activeRow.value = row
  popoverRef.value?.show(event)
}
function runAction(action: TableAction<T>): void {
  const row = activeRow.value
  if (!row) return
  if (action.disabled?.(row)) return
  action.onClick(row)
  popoverRef.value?.hide()
}

const visibleActionsForRow = computed<TableAction<T>[]>(() => {
  const row = activeRow.value
  if (!row || !props.actions) return []
  return props.actions.filter((a) => !a.hidden?.(row))
})

// ── Row click ─────────────────────────────────────────────────────────────
function handleRowClick(payload: { data: T }): void {
  if (props.loading || !props.clickable) return
  emit('row-click', payload.data)
}

defineExpose({ resetPage: () => goToPage(1) })
</script>

<template>
  <Box class="app-table">
    <!-- Header -->
    <!--
      The chrome row holds (left → right):
        [title/subtitle block]  [stretch spacer]  [#quick-filters]  [search/sort/#header-actions]  [filter button]

      `#quick-filters` is the canonical place for consumer-owned inline
      filter controls (e.g. a treasury-account picker, status pills) that
      should sit adjacent to the filter button rather than in a standalone
      row above the table. The empty spacer absorbs any free width so the
      title block (when present) stays left and the right-side cluster
      (quick filters + actions + filter button) sits together on the
      right. The slot itself takes natural width and never stretches.
      When unused it collapses gracefully (renders nothing).
    -->
    <Box
      v-if="
        title ||
        subtitle ||
        searchable ||
        sortOptions?.length ||
        filterable ||
        $slots['quick-filters'] ||
        $slots['header-actions']
      "
      flex
      align="center"
      class="app-table__header"
    >
      <Box flex direction="col" gap="1" class="app-table__title-block">
        <Text v-if="title" type="heading" weight="bold">{{ title }}</Text>
        <Text v-if="subtitle" type="caption" color="muted">{{ subtitle }}</Text>
      </Box>
      <Box class="app-table__spacer" />
      <Box
        v-if="$slots['quick-filters']"
        flex
        align="center"
        gap="3"
        class="app-table__quick-filters"
      >
        <slot name="quick-filters" />
      </Box>
      <Box flex align="center" gap="3" class="app-table__header-actions">
        <SearchInput
          v-if="searchable"
          v-model="searchValue"
          :placeholder="searchPlaceholder"
          class="app-table__search"
        />
        <Select
          v-if="sortOptions?.length"
          v-model="sortValue"
          :options="sortOptions"
          :placeholder="sortPlaceholder"
          size="small"
          class="app-table__sort"
        />
        <button
          v-if="filterable"
          type="button"
          class="app-table__filter-btn"
          :class="{ 'app-table__filter-btn--active': filterCount > 0 }"
          aria-label="Filters"
          @click="onFilterClick"
        >
          <PiIcon name="filter" size="sm" />
          <Box v-if="filterCount > 0" class="app-table__filter-count">
            <Text type="caption-sm" weight="bold" color="inverse">
              {{ filterCount }}
            </Text>
          </Box>
        </button>
        <slot name="header-actions" />
      </Box>
    </Box>

    <!-- Body -->
    <DataTable
      :value="tableRows"
      :is-loading="false"
      :columns="dtColumns"
      :paginator="false"
      :density="density"
      :flat="true"
      :scrollable="scrollable"
      :row-key="rowKey"
      :reorderableRows="reorderable || undefined"
      class="app-table__body"
      @row-click="handleRowClick"
      @row-reorder="(e) => emit('row-reorder', e)"
    >
      <!-- Forward consumer cell slots: `cell-<key>` — skeleton wins while loading -->
      <template v-for="(col, colIndex) in columns" :key="col.key" #[col.key]="slotProps">
        <slot v-if="loading" name="loading-cell" :col="col" :col-index="colIndex">
          <Skeleton height="14px" :width="colIndex === 0 ? '60%' : '40%'" />
        </slot>
        <slot
          v-else
          :name="`cell-${col.key}`"
          v-bind="slotProps"
          :row="(slotProps as { data: T }).data"
          :value="cellValue((slotProps as { data: T }).data, col.key)"
        >
          <Text type="body-sm">
            {{ cellValue((slotProps as { data: T }).data, col.key) ?? '—' }}
          </Text>
        </slot>
      </template>

      <!-- Actions kebab -->
      <template v-if="showActionsColumn" #[ACTIONS_KEY]="{ data }">
        <Skeleton v-if="loading" height="14px" width="14px" border-radius="4px" />
        <Button
          v-else
          variant="text"
          color="secondary"
          size="small"
          icon="ellipsis-v"
          aria-label="Row actions"
          class="app-table__kebab"
          @click.stop="openActions($event, data as T)"
        />
      </template>

      <!-- Chevron (clickable rows, no actions) -->
      <template v-else-if="showChevronColumn" #[ACTIONS_KEY]>
        <Skeleton v-if="loading" height="14px" width="14px" border-radius="4px" />
        <PiIcon v-else name="chevron-right" size="sm" class="app-table__chevron" />
      </template>

      <template #empty>
        <slot name="empty">
          <Box flex direction="col" align="center" gap="2" class="app-table__empty">
            <PiIcon name="inbox" size="xl" class="app-table__empty-icon" />
            <Text type="body" weight="semibold">{{ emptyTitle }}</Text>
            <Text type="caption" color="muted">{{ emptySubtitle }}</Text>
          </Box>
        </slot>
      </template>
    </DataTable>

    <!-- Footer — visible whenever there's at least one record. Three
         regions: summary (left), page indicator + size selector (middle),
         Prev / Next (right). Middle group wraps below on narrow widths. -->
    <Box
      v-if="totalRecords > 0 && !hideFooter"
      flex
      justify="between"
      align="center"
      gap="3"
      class="app-table__footer"
    >
      <Text type="caption" color="muted" class="app-table__footer-summary">{{ summary }}</Text>
      <Box flex align="center" gap="3" class="app-table__footer-middle">
        <Box v-if="totalPages > 0" flex align="center" gap="2" class="app-table__page-jump">
          <Text type="caption" color="muted">Page</Text>
          <input
            v-model="pageInputValue"
            type="number"
            :min="1"
            :max="totalPages"
            aria-label="Jump to page"
            class="app-table__page-input"
            @keydown="handlePageInputKeydown"
            @blur="commitPageInput"
          />
          <Text type="caption" color="muted">of {{ totalPages }}</Text>
        </Box>
        <Select
          v-model="pageSizeValue"
          :options="pageSizeSelectOptions"
          size="small"
          class="app-table__page-size"
          aria-label="Rows per page"
        />
      </Box>
      <Box flex gap="2" class="app-table__footer-nav">
        <Button
          label="Prev"
          variant="outline"
          color="secondary"
          size="small"
          :disabled="!canPrev"
          @click="goPrev"
        />
        <Button
          label="Next"
          variant="outline"
          color="secondary"
          size="small"
          :disabled="!canNext"
          @click="goNext"
        />
      </Box>
    </Box>

    <!-- Actions popover (single instance, content swaps per active row) -->
    <Popover v-if="showActionsColumn" ref="popoverRef">
      <Box flex direction="col" class="app-table__menu">
        <button
          v-for="action in visibleActionsForRow"
          :key="action.label"
          type="button"
          class="app-table__menu-item"
          :class="{
            'app-table__menu-item--danger': action.danger,
            'app-table__menu-item--disabled': activeRow ? action.disabled?.(activeRow as T) : false,
          }"
          :disabled="activeRow ? (action.disabled?.(activeRow as T) ?? false) : false"
          @click="runAction(action)"
        >
          <PiIcon v-if="action.icon" :name="action.icon" size="sm" class="app-table__menu-icon" />
          <Text type="body-sm">{{ action.label }}</Text>
        </button>
      </Box>
    </Popover>
  </Box>
</template>

<style scoped>
.app-table {
  display: flex;
  flex-direction: column;
  width: 100%;
  background: var(--p-surface-0);
  border: 1px solid var(--p-content-border-color);
  border-radius: var(--p-border-radius-lg);
  overflow: hidden;
}

.app-table__header {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--p-content-border-color);
  gap: 1rem;
}

.app-table__title-block {
  min-width: 0;
}

.app-table__header-actions {
  flex-shrink: 0;
}

.app-table__spacer {
  flex: 1 1 auto;
  min-width: 0;
  /* Absorbs any free chrome-row width so the title block (when present)
     stays left and the quick-filters + header-actions + filter-button
     cluster sits adjacent on the right. */
}

.app-table__quick-filters {
  flex: 0 0 auto;
  min-width: 0;
  /* Sit adjacent to the right-side action cluster — natural width, no
     stretch. Consumer-provided controls (e.g. a treasury-account Select,
     status pills) size themselves and the filter button stays right
     next to them. */
}

.app-table__search {
  width: 260px;
}

.app-table__sort {
  min-width: 180px;
}

.app-table__filter-btn {
  position: relative;
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--p-surface-0);
  border: 1px solid var(--p-content-border-color);
  border-radius: var(--p-border-radius-sm);
  color: var(--p-text-color);
  cursor: pointer;
  transition:
    background-color 0.12s ease,
    border-color 0.12s ease,
    color 0.12s ease;
  flex-shrink: 0;
}

.app-table__filter-btn:hover {
  background: var(--p-surface-100);
  border-color: var(--p-primary-300);
  color: var(--p-primary-600);
}

.app-table__filter-btn--active {
  background: var(--p-primary-50);
  border-color: var(--p-primary-500);
  color: var(--p-primary-600);
}

.app-table__filter-count {
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--p-primary-600);
  border-radius: 999px;
  border: 2px solid var(--p-surface-0);
}

.app-table__body {
  border-radius: 0;
}

.app-table__chevron {
  color: var(--p-text-muted-color);
}

.app-table__kebab {
  width: 32px;
  height: 32px;
  padding: 0;
}

/* ── Empty ── */
.app-table__empty {
  padding: 2.5rem 1.25rem;
  text-align: center;
}

.app-table__empty-icon {
  color: var(--p-text-muted-color);
}

/* ── Footer ── */
.app-table__footer {
  padding: 0.75rem 1.25rem;
  border-top: 1px solid var(--p-content-border-color);
  background: var(--p-surface-0);
  flex-wrap: wrap;
}

.app-table__footer-middle {
  flex-wrap: wrap;
}

.app-table__page-size {
  min-width: 88px;
}

.app-table__page-jump {
  flex-shrink: 0;
}

.app-table__page-input {
  width: 52px;
  height: 28px;
  padding: 0 0.25rem;
  text-align: center;
  font-family: inherit;
  font-size: 0.8125rem;
  color: var(--p-text-color);
  background: var(--p-surface-0);
  border: 1px solid var(--p-content-border-color);
  border-radius: var(--p-border-radius-sm);
  font-variant-numeric: tabular-nums;
  transition:
    border-color 0.12s ease,
    box-shadow 0.12s ease;
}

.app-table__page-input:hover {
  border-color: var(--p-primary-300);
}

.app-table__page-input:focus {
  outline: none;
  border-color: var(--p-primary-500);
  box-shadow: 0 0 0 2px var(--p-primary-100);
}

/* Hide native number spinner — operator types or uses Prev/Next */
.app-table__page-input::-webkit-outer-spin-button,
.app-table__page-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.app-table__page-input {
  -moz-appearance: textfield;
  appearance: textfield;
}

/* ── Popover menu (teleported — still reachable via the inner classes) ── */
.app-table__menu {
  padding: 0.25rem;
  min-width: 120px;
}

.app-table__menu-item {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.3rem 0.5rem;
  width: 100%;
  background: transparent;
  border: none;
  border-radius: var(--p-border-radius-sm);
  color: var(--p-text-color);
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  font-size: 0.8125rem;
  transition: background-color 0.12s ease;
}

.app-table__menu-item:hover:not(:disabled) {
  background: var(--p-surface-100);
}

.app-table__menu-item--danger {
  color: var(--p-danger-600);
}

.app-table__menu-item--disabled,
.app-table__menu-item:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
</style>
